import {
  Button,
  Flex,
  Group,
  Paper,
  Stack,
  Text,
  ThemeIcon,
} from "@mantine/core";
import { openConfirmModal } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import { IconArrowRight, IconFileTime } from "@tabler/icons-react";
import { useArchiveDraftOrder } from "@jasper/use-cases";

import { DashboardDraftFragment } from "/@/generated/graphql";
import { useResumeDraftOrder } from "/@/components/order/useResumeDraftOrder";

const relativeTime = new Intl.RelativeTimeFormat("fr-FR", {
  numeric: "auto",
  style: "short",
});

const RELATIVE_TIME_STEPS: [Intl.RelativeTimeFormatUnit, number][] = [
  ["minute", 60],
  ["hour", 24],
  ["day", 30],
  ["month", 12],
  ["year", Infinity],
];

const formatSince = (value: unknown) => {
  let elapsed = Math.round(
    (new Date(value as number).getTime() - Date.now()) / 60_000
  );

  for (const [unit, unitsInNext] of RELATIVE_TIME_STEPS) {
    if (Math.abs(elapsed) < unitsInNext) {
      return relativeTime.format(elapsed, unit);
    }
    elapsed = Math.round(elapsed / unitsInNext);
  }
};

const useDeleteDraft = (draft: DashboardDraftFragment) => {
  const { archiveDraftOrder } = useArchiveDraftOrder({
    refetchQueries: [
      "GetDashboardLastDraft",
      "GetDashboardCounts",
      "getUserNotificationsCount",
    ],
  });

  return () =>
    openConfirmModal({
      title: "Supprimer le brouillon",
      centered: true,
      children: (
        <Text size="sm">
          Voulez-vous vraiment supprimer le brouillon {draft.reference} ?
        </Text>
      ),
      labels: { confirm: "Supprimer", cancel: "Annuler" },
      confirmProps: { color: "danger" },
      onConfirm: async () => {
        try {
          await archiveDraftOrder(draft.id);
          notifications.show({
            color: "green",
            title: "Brouillon supprimé",
            message: "",
          });
        } catch {
          notifications.show({
            color: "red",
            title: "Le brouillon n'a pas pu être supprimé",
            message: "",
          });
        }
      },
    });
};

export const DraftResumeCard = ({
  draft,
  otherDraftsCount,
}: {
  draft: DashboardDraftFragment;
  otherDraftsCount: number;
}) => {
  const resumeDraftOrder = useResumeDraftOrder();
  const openDeleteModal = useDeleteDraft(draft);
  const productsCount = draft.products.length;
  const resume = () =>
    resumeDraftOrder({
      id: draft.id,
      createdAt: draft.createdAt,
      productTypes: draft.products.map(product => product.type),
    });

  return (
    <Paper
      radius="lg"
      bg="brand.0"
      bd="1px solid var(--mantine-color-brand-2)"
      py="s4"
      px="s5"
    >
      <Flex
        direction={{ base: "column", md: "row" }}
        align={{ base: "stretch", md: "center" }}
        gap="md"
      >
        <Group wrap="nowrap" flex={1} miw={0}>
          <ThemeIcon size={42} radius="md" bg="brand.1" c="brand.5">
            <IconFileTime size={20} />
          </ThemeIcon>
          <Stack gap={2} flex={1} miw={0}>
            <Group gap="s3" align="baseline">
              <Text fz={11} fw={600} tt="uppercase" lts="0.04em" c="brand.5">
                Brouillon en cours
              </Text>
              {otherDraftsCount > 0 && (
                <Text fz={12} fw={500} c="brand.5">
                  + {otherDraftsCount} autre{otherDraftsCount > 1 ? "s" : ""}
                </Text>
              )}
            </Group>
            <Text fz={16} fw={600} c="ink.9" truncate="end">
              {productsCount} prothèse{productsCount > 1 ? "s" : ""}
              {draft.patient &&
                ` · ${draft.patient.firstName} ${draft.patient.lastName}`}
            </Text>
            <Text fz={12.5} c="ink.4">
              <Text span fw={600} inherit c="ink.6">
                {draft.reference}
              </Text>{" "}
              · modifié {formatSince(draft.updatedAt)}
            </Text>
          </Stack>
        </Group>
        <Group gap="xs" wrap="nowrap">
          <Button
            flex={{ base: 1, md: "none" }}
            color="danger"
            onClick={openDeleteModal}
          >
            Supprimer
          </Button>
          <Button
            flex={{ base: 1, md: "none" }}
            rightSection={<IconArrowRight size={16} />}
            onClick={resume}
          >
            Reprendre
          </Button>
        </Group>
      </Flex>
    </Paper>
  );
};
